import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ErrorAlert } from '@/components/common/ErrorAlert';

export type CloseType = 'NoGo' | 'Closed';

interface CloseLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  closeType: CloseType;
  onConfirm: (closeType: CloseType, reason: string) => void;
  isLoading?: boolean;
}

const CLOSE_TYPE_LABELS: Record<CloseType, string> = {
  NoGo: 'No Go',
  Closed: 'Closed',
};

const CLOSE_TYPE_DESCRIPTIONS: Record<CloseType, string> = {
  NoGo: 'Lead declined - customer decided not to proceed with the work.',
  Closed: 'Job completed and paid in full.',
};

/**
 * Dialog for closing a lead with a specific close type
 * Shows a required text area for entering reason for closing
 */
export function CloseLeadDialog({
  open,
  onOpenChange,
  closeType,
  onConfirm,
  isLoading = false,
}: CloseLeadDialogProps) {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleConfirm = () => {
    setValidationError(null);

    // Validate reason is required
    if (!reason.trim()) {
      setValidationError('Reason for closing is required');
      return;
    }

    onConfirm(closeType, reason.trim());
  };

  const handleCancel = () => {
    setReason('');
    setValidationError(null);
    onOpenChange(false);
  };

  // Reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason('');
      setValidationError(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Close Lead - {CLOSE_TYPE_LABELS[closeType]}</DialogTitle>
          <DialogDescription>{CLOSE_TYPE_DESCRIPTIONS[closeType]}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <ErrorAlert error={validationError} />

          {/* Reason Field */}
          <div className="space-y-2">
            <Label htmlFor="closeReason">
              Reason for Closing <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="closeReason"
              placeholder="Enter reason for closing this lead..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={isLoading}
              autoFocus
              className="resize-none"
            />
            <p className="text-sm text-gray-500">
              Please provide details about why this lead is being closed
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="action" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Closing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
